import { useCallback, useSyncExternalStore } from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from './utils';

/**
 * Light / dark theme. Light is the default; the visitor's choice is stored in localStorage.
 * index.html (theme-init.js) applies the class before first paint, so there is no flash.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const THEME_COLOR = { light: '#FFFFFF', dark: '#0F0F0F' } as const;
const listeners = new Set<() => void>();

const current = (): Theme =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light';

function apply(theme: Theme, animate: boolean) {
  const root = document.documentElement;
  if (animate) {
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 350);
  }
  root.classList.toggle('dark', theme === 'dark');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, current, () => 'light' as Theme);
  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode: the choice lasts for this page only */
    }
    apply(next, true);
  }, []);
  const toggle = useCallback(() => setTheme(current() === 'dark' ? 'light' : 'dark'), [setTheme]);
  return { theme, setTheme, toggle };
}

/** Sun / moon button. `label` receives the theme the click switches to. */
export function ThemeToggle({
  className,
  label,
}: {
  className?: string;
  label: (next: Theme) => string;
}) {
  const { theme, toggle } = useTheme();
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  const Icon = theme === 'dark' ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label(next)}
      title={label(next)}
      className={cn('inline-flex items-center justify-center rounded-lg', className)}
    >
      <Icon className="size-[19px]" strokeWidth={1.75} aria-hidden />
    </button>
  );
}
